package com.schoolconnect.app.repository.exam

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.ExamCreateRequest
import com.schoolconnect.app.datasource.network.request.ExamUpdateRequest
import com.schoolconnect.app.datasource.network.response.ExamResponse
import com.schoolconnect.app.datasource.network.response.TeacherExamListResponse
import com.schoolconnect.app.datasource.network.response.TeacherExamStatisticsResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.tenantCacheKey
import kotlinx.coroutines.CoroutineScope

sealed interface ExamScope {
    data object All : ExamScope
    data class School(val schoolId: String) : ExamScope
    data class AcademicYear(val academicYearId: String) : ExamScope
    data class ClassSection(val classSectionId: String) : ExamScope
    data class Teacher(val teacherId: String) : ExamScope
    data class User(val userId: String) : ExamScope
}

data class ExamsPageResult(val items: List<ExamResponse>, val totalCount: Int)
data class TeacherExamsPageResult(val response: TeacherExamListResponse, val totalCount: Int)

object ExamRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ExamRepository"
    private val teacherPageCache = RepositoryCache<TeacherExamsPageResult>()

    fun invalidateTeacherPage() {
        teacherPageCache.clear()
        scLog("$TAG invalidateTeacherPage")
    }

    suspend fun create(
        request: ExamCreateRequest
    ): Result<ExamResponse> = KtorApiClient.createExam(request).onSuccess { teacherPageCache.clear() }

    suspend fun update(
        examId: String,
        request: ExamUpdateRequest
    ): Result<ExamResponse> = KtorApiClient.updateExam(examId, request).onSuccess { teacherPageCache.clear() }

    suspend fun delete(examId: String): Result<Unit> = KtorApiClient.deleteExam(examId)
        .onSuccess { teacherPageCache.clear() }
        .onFailure { throwable -> scLog("$TAG delete failure examId=$examId", throwable) }

    suspend fun get(examId: String): Result<ExamResponse> =
        KtorApiClient.getExam(examId)
            .onFailure { throwable -> scLog("$TAG get failure examId=$examId", throwable) }

    /**
     * The backend list endpoint is scoped to one class section. Teacher pages
     * span all assigned sections, so fetch a bounded page for each section and
     * merge it before applying the shared table pagination.
     */
    suspend fun loadPage(
        classSectionIds: List<String>,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sortBy: String? = null,
        order: String? = null
    ): Result<ExamsPageResult> {
        val ids = classSectionIds.map(String::trim).filter(String::isNotBlank).distinct()
        if (ids.isEmpty()) return Result.success(ExamsPageResult(emptyList(), 0))
        val safeLimit = pageSize.coerceIn(1, 100)
        val results = ids.map { id ->
            KtorApiClient.getExams(id, limit = safeLimit, offset = 0, sortBy = sortBy, order = order)
        }
        val failure = results.firstOrNull { it.isFailure }
        if (failure != null) {
            val error = failure.exceptionOrNull() ?: IllegalStateException("Unable to load exams")
            scLog("$TAG loadPage failure sections=${ids.size}", error)
            return Result.failure(error)
        }
        val items = results.flatMap { it.getOrNull().orEmpty() }
        val total = results.sumOf { it.getOrNull()?.serverTotalCountOrNull() ?: it.getOrNull()?.size ?: 0 }
        scLog("$TAG loadPage success sections=${ids.size} items=${items.size} total=$total")
        return Result.success(ExamsPageResult(items, total))
    }

    suspend fun loadStatistics(): Result<TeacherExamStatisticsResponse> =
        KtorApiClient.getTeacherExamStatistics()
            .onFailure { throwable -> scLog("$TAG loadStatistics failure", throwable) }

    suspend fun loadTeacherPage(
        search: String?,
        status: String?,
        classSectionId: String?,
        pageIndex: Int,
        pageSize: Int,
        sortBy: String?,
        order: String?,
        forceRemote: Boolean = false,
        refreshScope: CoroutineScope
    ): Result<TeacherExamsPageResult> {
        val key = tenantCacheKey("teacher-exams:${search.orEmpty()}:${status.orEmpty()}:${classSectionId.orEmpty()}:$pageIndex:$pageSize:${sortBy.orEmpty()}:${order.orEmpty()}")
        return teacherPageCache.load(refreshScope, TAG, "loadTeacherPage", key, forceRemote = forceRemote) { KtorApiClient.getTeacherExams(
        search = search,
        status = status,
        classSectionId = classSectionId,
        pageIndex = pageIndex,
        pageSize = pageSize,
        sortBy = sortBy,
        order = order
    ).map { response ->
        // The list endpoint documents count as the filtered result total. A
        // missing count is a malformed response; the counters and current-page
        // item count are not valid substitutes for pagination.
        val total = response.count
            ?: error("Teacher exams response is missing the filtered count.")
        TeacherExamsPageResult(response, total)
    }.onFailure { throwable -> scLog("$TAG loadTeacherPage failure", throwable) } }
    }
}
